/** Shared basin and finite geological surfaces. Radii are planet-local metres.
 *
 * Basin profiles are OPT IN. Finite lava comes from explicit sampled geology.
 * Grounded ice fills a basin down to its bed. It is not a floating slab and
 * cannot represent leads, overhangs, an underside, melting or breakable ice.
 * Lava is a non-swimmable liquid. Contact is reported, not assigned damage here.
 */
import type { PlanetDef, PlanetSampler, TerrainSample } from './universe.js';

export type SeaSurface = 'water' | 'grounded-ice' | 'lava';
export type SurfaceMaterial = 'terrain' | 'ice' | 'water' | 'lava';
export const NO_SURFACE = -1e9;

export function seaSurface(def: Pick<PlanetDef, 'seaSurface'> | undefined): SeaSurface {
  // Terrain-only hosts/test adapters predate profiles and retain solid ground.
  return def?.seaSurface ?? 'water';
}

export interface SurfaceColumn {
  /** Rock bed below any grounded glacier or basin ice. */
  bedR: number;
  /** Highest continuous solid; grounded ice includes its entire buried volume. */
  supportR: number;
  supportMaterial: 'terrain' | 'ice';
  /** Visible/contact boundary, including liquid. This is NOT foot support. */
  topR: number;
  topMaterial: SurfaceMaterial;
  liquid: 'none' | 'water' | 'lava';
  liquidR: number;
  liquidDepth: number;
  /** Water-only candidate level and signed depth. Negative depth means dry.
   * Preserve legacy signed queries even where the candidate lies below land. */
  waterR: number;
  waterDepth: number;
  seaWaterDepth: number;
  riverK: number;
  flowSpeed: number;
  fx: number;
  fy: number;
  fz: number;
}

export function createSurfaceColumn(): SurfaceColumn {
  return { bedR: 0, supportR: 0, supportMaterial: 'terrain', topR: 0,
    topMaterial: 'terrain', liquid: 'none', liquidR: NO_SURFACE, liquidDepth: 0,
    waterR: NO_SURFACE, waterDepth: NO_SURFACE, seaWaterDepth: NO_SURFACE,
    riverK: 0, flowSpeed: 0, fx: 0, fy: 0, fz: 0 };
}

const sample: TerrainSample = {
  h: 0, temp: 0, moist: 0, river: 0, mountain: 0,
  flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8,
};

/** One sample, no per-query allocation; shared by client and server.
 * Rivers remain freshwater above the basin boundary. This does not simulate
 * water/lava mixing, freezing, or transport through solid ice. */
export function surfaceColumn(
  sampler: Pick<PlanetSampler, 'sample' | 'amplitude'>, def: PlanetDef,
  dx: number, dy: number, dz: number, out: SurfaceColumn,
): SurfaceColumn {
  sampler.sample(dx, dy, dz, sample, false);
  return surfaceColumnFromSample(def, sampler.amplitude, sample, out);
}

/** Reuse a climate/terrain sample already obtained by weather or placement. */
export function surfaceColumnFromSample(def: PlanetDef, amplitude: number, sample: TerrainSample, out: SurfaceColumn): SurfaceColumn {
  const solid = def.radius + sample.h * amplitude;
  const bed = solid - (sample.glacierIceM ?? 0);
  const sea = def.seaRadius ?? NO_SURFACE, kind = seaSurface(def);
  const support = kind === 'grounded-ice' ? Math.max(solid, sea) : solid;
  const riverWs = sample.riverWs ?? NO_SURFACE;
  const riverR = riverWs > -1e8 ? def.radius + riverWs * amplitude : NO_SURFACE;
  const wetlandM=sample.wetlandWaterM??NO_SURFACE;
  const wetlandR=wetlandM>-1e8?def.radius+wetlandM:NO_SURFACE;
  let waterR = kind === 'water' ? Math.max(sea, riverR, wetlandR) : Math.max(riverR,wetlandR);
  const localLava=(sample.lavaSurfaceM??NO_SURFACE)>-1e8&&(sample.lavaDepthM??0)>0
    ?def.radius+sample.lavaSurfaceM!:NO_SURFACE;
  // Finite flows are generated above sea level, outside freshwater. A lava
  // column cannot accidentally advertise freshwater below its exposed top.
  if(localLava>support&&waterR<=localLava)waterR=NO_SURFACE;
  // An inland reach can be above ice/lava, but cannot create hidden water
  // inside solid ice or freshwater below a molten basin's contact boundary.
  if (kind !== 'water' && waterR <= Math.max(sea, support)) waterR = NO_SURFACE;
  const waterDepth = waterR > -1e8 ? waterR - support : NO_SURFACE;
  const lavaR = waterDepth<=0?Math.max(localLava,kind==='lava'&&sea>support?sea:NO_SURFACE):NO_SURFACE;
  out.bedR = bed;
  out.supportR = support;
  out.supportMaterial = support > bed ? 'ice' : 'terrain';
  out.waterR = waterR;
  out.waterDepth = waterDepth;
  out.seaWaterDepth = kind === 'water' && def.seaRadius !== null ? sea - support : NO_SURFACE;
  out.liquid = waterDepth > 0 ? 'water' : lavaR > support ? 'lava' : 'none';
  out.liquidR = out.liquid === 'water' ? waterR : out.liquid === 'lava' ? lavaR : NO_SURFACE;
  out.liquidDepth = out.liquid === 'none' ? 0 : out.liquidR - support;
  out.topR = out.liquid === 'none' ? support : out.liquidR;
  out.topMaterial = out.liquid === 'none' ? out.supportMaterial : out.liquid;
  // Preserve every existing water/current field for unprofiled worlds. On
  // non-water basins only an exposed freshwater reach can carry a current.
  const riverActive = (kind === 'water' || waterDepth > 0) && out.liquid!=='lava';
  out.riverK = riverActive ? sample.river : 0;
  out.flowSpeed = riverActive ? sample.riverSpeed ?? 0 : 0;
  out.fx = riverActive ? sample.flowx : 0;
  out.fy = riverActive ? sample.flowy : 0;
  out.fz = riverActive ? sample.flowz : 0;
  return out;
}

/** Solid-only fast path: water and lava cannot support feet. */
export function surfaceSupportRadius(sampler: PlanetSampler, dx: number, dy: number, dz: number): number {
  const bed = sampler.surfaceRadius(dx, dy, dz);
  return seaSurface(sampler.def) === 'grounded-ice' ? Math.max(bed, sampler.def.seaRadius ?? NO_SURFACE) : bed;
}

export function surfaceSupportNormal(sampler: PlanetSampler, dx: number, dy: number, dz: number, out: number[], eps = .5): void {
  if (seaSurface(sampler.def) === 'grounded-ice' && sampler.def.seaRadius !== null
    && sampler.surfaceRadius(dx, dy, dz) < sampler.def.seaRadius) {
    out[0] = dx; out[1] = dy; out[2] = dz;
  } else sampler.surfaceNormal(dx, dy, dz, out, eps);
}

/** Existing projectile/flight boundary includes the sea (liquid absorbs a bolt).
 * Kept distinct from solid support and from river-aware hover sampling. */
export function surfaceBoundaryRadius(sampler: PlanetSampler, dx: number, dy: number, dz: number): number {
  if(sampler.def.archetype==='volcanic')return surfaceColumn(sampler,sampler.def,dx,dy,dz,boundaryColumn).topR;
  // Preserve the existing sea/ground boundary while adding the finite local
  // basin top. Ordinary river projectile semantics remain unchanged.
  sampler.sample(dx,dy,dz,boundarySample,false);
  const local=boundarySample.wetlandWaterM??NO_SURFACE;
  return Math.max(sampler.def.radius+boundarySample.h*sampler.amplitude,sampler.def.seaRadius??-Infinity,
    local>-1e8?sampler.def.radius+local:-Infinity);
}
const boundaryColumn=createSurfaceColumn();
const boundarySample={}as TerrainSample;

/** Ground agents may wade water up to their own limit. Any exposed lava is
 * excluded even when shallower than that limit; grounded ice is traversable. */
export function surfaceAvoidanceDepth(column: SurfaceColumn): number {
  if (column.liquid === 'lava') return Infinity;
  if (column.liquid === 'none' && column.supportMaterial === 'ice') return 0;
  return column.waterDepth;
}

/** Feet touching exposed lava use the existing hazard/protection pipeline.
 * A shallow channel is escapable; this is gradual damage, never instant death.
 * Callers take max(ambientHeat, contactHeat), not the sum. */
export const LAVA_CONTACT_HP_PER_SECOND=12;
export function lavaContactRate(column:SurfaceColumn,feetRadius:number):number {
  return column.liquid==='lava'&&feetRadius<=column.liquidR+.04&&feetRadius>=column.supportR-.15
    ?LAVA_CONTACT_HP_PER_SECOND:0;
}

/** Material occupied by a point. Exact boundaries count as contact. Damage,
 * equipment protection and VFX policy belong to their respective consumers. */
export function surfaceContact(column: SurfaceColumn, radius: number): SurfaceMaterial | 'air' {
  if (radius <= column.bedR) return 'terrain';
  if (radius <= column.supportR) return column.supportMaterial;
  if (column.liquid !== 'none' && radius <= column.liquidR) return column.liquid;
  return 'air';
}
